/**
 * Shared notification-category presentation helpers.
 *
 * The notification-type registry (GET /api/email/notification-types) carries a
 * `category` per type. These helpers turn that into a consistent, grouped display
 * so the Email Settings page and the Email Recipients dialog stay in lockstep
 * (single source of truth — no per-page drift).
 */

// Human-friendly labels per category, in the order they should be displayed.
export const CATEGORY_LABELS = {
  approvals: 'Approvals & Verifications',
  lifecycle: 'Contract Lifecycle',
  reminders: 'Scheduled Reminders (internal digests)',
  payments: 'Payments & Money Movement',
  portal: 'Client Portal & Onboarding',
  other: 'Other',
};

// Stable display order; any category not listed falls back after these (then 'other').
const CATEGORY_ORDER = ['approvals', 'lifecycle', 'reminders', 'payments', 'portal', 'other'];

/**
 * Group notification types by category into an ordered array.
 * @param {Array<{key:string,label:string,category?:string}>} types
 * @returns {Array<{ category: string, label: string, items: Array }>}
 */
export const groupByCategory = (types = []) => {
  const buckets = {};
  for (const t of types) {
    const cat = t.category || 'other';
    (buckets[cat] = buckets[cat] || []).push(t);
  }
  // Known categories first (in CATEGORY_ORDER), then any unknown ones alphabetically.
  const known = CATEGORY_ORDER.filter(c => buckets[c]);
  const unknown = Object.keys(buckets).filter(c => !CATEGORY_ORDER.includes(c)).sort();
  return [...known, ...unknown].map(category => ({
    category,
    label: CATEGORY_LABELS[category] || category,
    items: buckets[category],
  }));
};
